import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';

import {environment} from '../../../environments/environment';

export const READONLY_RUNTIME_MODE = 'shared-production-readonly' as const;

export interface RuntimeCapabilityFlags {
  customerData: false;
  mutations: false;
  routes: false;
  schedulers: false;
  workers: false;
  webhooks: false;
  hubspotRequests: false;
  providerRequests: false;
  cosmosItemReads: false;
  cosmosQueries: false;
  cosmosWrites: false;
  cosmosMetadataValidation: true;
}

export interface RuntimeCapabilities {
  mode: typeof READONLY_RUNTIME_MODE;
  environment: 'staging';
  targetClass: 'shared-production';
  readiness: 'starting' | 'ready' | 'failed';
  contractDigest: string;
  sourceSha: string;
  imageDigest: string;
  capabilities: RuntimeCapabilityFlags;
}

const EXPECTED_CAPABILITIES: RuntimeCapabilityFlags = {
  customerData: false,
  mutations: false,
  routes: false,
  schedulers: false,
  workers: false,
  webhooks: false,
  hubspotRequests: false,
  providerRequests: false,
  cosmosItemReads: false,
  cosmosQueries: false,
  cosmosWrites: false,
  cosmosMetadataValidation: true,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasExactCapabilityFlags = (value: unknown): value is RuntimeCapabilityFlags => {
  if (!isRecord(value)) return false;
  const expectedKeys = Object.keys(EXPECTED_CAPABILITIES).sort();
  const actualKeys = Object.keys(value).sort();
  return (
    actualKeys.length === expectedKeys.length &&
    actualKeys.every((key, index) => key === expectedKeys[index]) &&
    expectedKeys.every((key) => value[key] === EXPECTED_CAPABILITIES[key as keyof RuntimeCapabilityFlags])
  );
};

export const parseRuntimeCapabilities = (value: unknown): RuntimeCapabilities => {
  if (
    !isRecord(value) ||
    value['mode'] !== READONLY_RUNTIME_MODE ||
    value['environment'] !== 'staging' ||
    value['targetClass'] !== 'shared-production' ||
    !['starting', 'ready', 'failed'].includes(String(value['readiness'])) ||
    typeof value['contractDigest'] !== 'string' ||
    !/^sha256:[a-f0-9]{64}$/.test(value['contractDigest']) ||
    typeof value['sourceSha'] !== 'string' ||
    !/^[a-f0-9]{40}$/.test(value['sourceSha']) ||
    typeof value['imageDigest'] !== 'string' ||
    !/^sha256:[a-f0-9]{64}$/.test(value['imageDigest']) ||
    !hasExactCapabilityFlags(value['capabilities'])
  ) {
    throw new Error('runtime_capabilities_contract_invalid');
  }

  return value as unknown as RuntimeCapabilities;
};

@Injectable({providedIn: 'root'})
export class RuntimeCapabilitiesService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.enrichBaseServerUrl.replace(/\/$/, '')}/api/runtime-capabilities`;

  getRuntimeCapabilities(): Observable<RuntimeCapabilities> {
    return this.http.get<unknown>(this.endpoint).pipe(map(parseRuntimeCapabilities));
  }
}
